/**
 * @bos/portal3 — THE LIFECYCLE PICTURE (phase 2): one machine as a diagram you can touch.
 *
 * A thin renderer over `lifecycleLayout.ts` — the geometry is decided there; this file draws it
 * and turns gestures into the stage's own typed orders. The same three laws the ER canvas keeps:
 *
 *   1. ONE PAYLOAD, TWO VIEWS. The diagram draws the same `LifecyclesDoc.machines[]` row the
 *      MachineCard beneath it renders — no second fetch, no second source. The card stays: it is
 *      the keyboard/screen-reader path in full sentences, and it carries the dossier's own
 *      verification sentence, which a picture cannot say.
 *   2. THE PICTURE IS A WAY TO REACH THE DOOR, NEVER A SECOND DOOR. Every gesture compiles to
 *      exactly one existing `sm-*` change and opens the SAME `StageEditDoor` composer, prefilled
 *      — same dry run, same basket, same apply. Nothing here commits anything.
 *   3. NO AFFORDANCE FOR A DOOR THAT DOES NOT EXIST. The vocabulary is state add/rename/remove,
 *      transition add/remove and terminal-set, so those are the gestures. A move with NO recorded
 *      event offers no remove, because the change names all three parts and would always be
 *      refused — the same "better absent than a control that always refuses" rule the composer
 *      already applies to that dropdown.
 *
 * WHAT A GUARD IS ALLOWED TO DO HERE: nothing but be read. ◆ on a move reveals the machine's own
 * guard sentence and the rule it enforces, verbatim, in a panel below — there is no exact change
 * for a guard, so there is no control that pretends there is.
 *
 * ACCESSIBILITY. One keyboard stop per state (Tab to it, Enter or Space opens it), and the acts
 * that follow are real buttons in the panel below the picture, not SVG text. Drag-to-connect is a
 * MOUSE SHORTCUT only — its keyboard equal is the composer's own "Allow a new move between
 * states", which is on the same screen and needs no picture at all.
 */
import { useMemo, useRef, useState } from 'react';
import {
  STATE_H,
  layoutLifecycle,
  gapLabel,
  moveLabel,
  type LcLayoutInput,
  type LcNode,
} from './lifecycleLayout';

/** The five gestures, one per existing `sm-*` kind the picture can honestly reach. The entity is
 *  bound by the caller, so nothing here has to carry it around. */
export interface LifecycleGestures {
  readonly onRenameState: (state: string) => void;
  readonly onRemoveState: (state: string) => void;
  readonly onSetTerminal: (state: string, terminal: boolean) => void;
  readonly onAddTransition: (from: string, to: string) => void;
  readonly onRemoveTransition: (from: string, to: string, event: string) => void;
}

export interface LifecycleDiagramProps {
  /** The thing that moves through these states — the machine's own `entity`. */
  readonly entity: string;
  /** The machine AFTER the basket, so pending states and moves can be drawn as pending. */
  readonly machine: LcLayoutInput;
  /** null = the door is closed: the picture is a read surface and offers no change. */
  readonly gestures: LifecycleGestures | null;
}

export function LifecycleDiagram({ entity, machine, gestures }: LifecycleDiagramProps) {
  const layout = useMemo(() => layoutLifecycle(machine), [machine]);
  /** The state whose acts are open below, and which highlights its own moves. */
  const [selected, setSelected] = useState<string | null>(null);
  /** The move whose guard is revealed below (its index in `machine.transitions`). */
  const [guardIndex, setGuardIndex] = useState<number | null>(null);
  /** Where a drag started. A mouse shortcut; never the only way to reach the change. */
  const dragFrom = useRef<string | null>(null);

  const markerId = `lc-arrow-${entity}`;
  const stateCount = machine.states.length;
  const moveCount = layout.edges.length;
  const terminal = new Set(machine.terminal);
  const guarded = guardIndex !== null ? machine.transitions[guardIndex] : undefined;

  function toggle(state: string) {
    setSelected((prior) => (prior === state ? null : state));
  }

  return (
    <div className="lc" data-machine={entity}>
      <div className="lc-canvas">
        <svg
          className="lc-svg"
          width={layout.width}
          height={layout.height}
          viewBox={`0 0 ${layout.width} ${layout.height}`}
          role="img"
          aria-label={`How a ${entity} moves: ${stateCount} ${stateCount === 1 ? 'state' : 'states'}, ${moveCount} ${moveCount === 1 ? 'move' : 'moves'}`}
          onPointerLeave={() => (dragFrom.current = null)}
        >
          <defs>
            <marker
              id={markerId}
              viewBox="0 0 10 10"
              refX="9"
              refY="5"
              markerWidth="7"
              markerHeight="7"
              orient="auto-start-reverse"
            >
              <path d="M 0 1 L 9 5 L 0 9 z" className="lc-arrowhead" />
            </marker>
          </defs>

          {/* Moves first — under the pills. */}
          {layout.edges.map((e) => (
            <path
              key={`m${e.index}`}
              className={`lc-edge${e.pending ? ' lc-edge--pending' : ''}${e.bus ? ' lc-edge--bus' : ''}${selected !== null && (e.from === selected || e.to === selected) ? ' lc-edge--selected' : ''}`}
              data-move={`${e.from}>${e.to}${e.event !== null ? `>${e.event}` : ''}`}
              d={e.d}
              markerEnd={`url(#${markerId})`}
            />
          ))}

          {/* The pills. */}
          {layout.nodes.map((node) => (
            <StatePill
              key={node.state}
              node={node}
              markerId={markerId}
              selected={selected === node.state}
              interactive={gestures !== null}
              onToggle={toggle}
              onDragStart={(name) => {
                if (gestures !== null) dragFrom.current = name;
              }}
              onDrop={(name) => {
                const from = dragFrom.current;
                dragFrom.current = null;
                if (gestures !== null && from !== null && from !== name) gestures.onAddTransition(from, name);
              }}
            />
          ))}

          {/* Labels last, each with a halo, so they stay readable over everything. */}
          {layout.edges.map((e) => {
            const text = e.bus ? moveLabel(e) : gapLabel(e);
            const removable = gestures !== null && e.event !== null;
            return (
              <g key={`l${e.index}`} className={`lc-elabel${e.bus ? ' lc-elabel--bus' : ''}`}>
                <text
                  className="lc-elabel-halo"
                  x={e.labelX}
                  y={e.labelY}
                  textAnchor="middle"
                  aria-hidden="true"
                >
                  {text}
                  {e.guard !== null && ' ◆'}
                </text>
                <text className="lc-elabel-text" x={e.labelX} y={e.labelY} textAnchor="middle">
                  <tspan
                    className={removable ? 'lc-act' : undefined}
                    data-gesture={removable ? 'transition-remove' : undefined}
                    data-move={`${e.from}>${e.to}`}
                    onClick={
                      removable
                        ? (event) => {
                            event.stopPropagation();
                            gestures.onRemoveTransition(e.from, e.to, e.event as string);
                          }
                        : undefined
                    }
                  >
                    {text}
                    {removable && (
                      <title>
                        Stop {e.from} → {e.to} being allowed
                      </title>
                    )}
                  </tspan>
                  {e.guard !== null && (
                    <tspan
                      className="lc-guardmark lc-act"
                      data-gesture="guard-read"
                      onClick={(event) => {
                        event.stopPropagation();
                        setGuardIndex(e.index);
                      }}
                    >
                      {' ◆'}
                      <title>This move only happens when something is true — read it</title>
                    </tspan>
                  )}
                </text>
                {e.pending && (
                  <text className="lc-pendingmark" x={e.labelX} y={e.labelY + 12} textAnchor="middle">
                    in the basket
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>

      <div className="lc-panel">
        {selected !== null && gestures !== null && (
          <div className="lc-acts" role="group" aria-label={`Change ${selected}`}>
            <span className="lc-acts__what">{selected}</span>
            <button
              className="btn btn--quiet btn--sm"
              type="button"
              data-gesture="state-rename"
              onClick={() => gestures.onRenameState(selected)}
            >
              Call it something else
            </button>
            <button
              className="btn btn--quiet btn--sm"
              type="button"
              data-gesture="state-remove"
              onClick={() => gestures.onRemoveState(selected)}
            >
              Remove it
            </button>
            <button
              className="btn btn--quiet btn--sm"
              type="button"
              data-gesture="terminal-set"
              onClick={() => gestures.onSetTerminal(selected, !terminal.has(selected))}
            >
              {terminal.has(selected) ? 'It can still move on' : 'Nothing happens after it'}
            </button>
          </div>
        )}
        {guarded !== undefined && (
          <div className="lc-guard" role="note" aria-label="When this move is allowed">
            <span className="lc-guard__move">
              {guarded.from} → {guarded.to}
              {guarded.event !== null && ` on ${guarded.event}`}
            </span>
            <p className="lc-guard__text">only when: {guarded.guard}</p>
            {guarded.enforcesRule !== null && (
              <p className="lc-guard__rule">enforces {guarded.enforcesRule}</p>
            )}
            <button className="btn btn--quiet btn--sm" type="button" onClick={() => setGuardIndex(null)}>
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

interface StatePillProps {
  readonly node: LcNode;
  readonly markerId: string;
  readonly selected: boolean;
  readonly interactive: boolean;
  readonly onToggle: (state: string) => void;
  readonly onDragStart: (state: string) => void;
  readonly onDrop: (state: string) => void;
}

function StatePill({ node, markerId, selected, interactive, onToggle, onDragStart, onDrop }: StatePillProps) {
  const name = node.state;
  const midY = node.y + STATE_H / 2;
  return (
    <g
      className={`lc-state${selected ? ' lc-state--selected' : ''}${node.pending ? ' lc-state--pending' : ''}${node.terminal ? ' lc-state--terminal' : ''}`}
      data-state={name}
      tabIndex={0}
      role="button"
      aria-label={`${name}${node.initial ? ' — where it starts' : ''}${node.terminal ? ' — the end of the road' : ''}${node.pending ? ' — in the basket' : ''}`}
      aria-current={selected ? 'true' : undefined}
      onClick={() => onToggle(name)}
      onPointerDown={() => onDragStart(name)}
      onPointerUp={() => onDrop(name)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onToggle(name);
        }
      }}
    >
      {node.initial && (
        <path
          className="lc-initial"
          d={`M ${node.x - 22} ${midY} L ${node.x - 4} ${midY}`}
          markerEnd={`url(#${markerId})`}
        />
      )}
      <rect className="lc-pill" x={node.x} y={node.y} width={node.w} height={STATE_H} rx={STATE_H / 2} />
      {node.terminal && (
        <rect
          className="lc-pill-ring"
          x={node.x + 3.5}
          y={node.y + 3.5}
          width={node.w - 7}
          height={STATE_H - 7}
          rx={(STATE_H - 7) / 2}
        />
      )}
      <text className="lc-statename" x={node.x + node.w / 2} y={midY + 4} textAnchor="middle">
        {name}
      </text>
      {node.pending && (
        <text className="lc-pendingmark" x={node.x + node.w / 2} y={node.y + STATE_H + 11} textAnchor="middle">
          in the basket
        </text>
      )}
      {interactive && <title>Open {name}</title>}
    </g>
  );
}
